// Session gate, routes and the habit editor shared by all screens.
import { LoaderCircle, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppUser, Habit, HabitInput } from '../shared/api';
import { Backdrop } from './components/Backdrop';
import { EditorContext, type EditorApi } from './components/EditorContext';
import { HabitEditor } from './components/HabitEditor';
import { Logo } from './components/Logo';
import { Shell } from './components/Shell';
import { useToast } from './components/Toasts';
import { api, onUnauthorized } from './lib/api';
import { signOut } from './lib/authClient';
import { navigate, parseRoute, useLocation } from './lib/router';
import { readJson, writeJson } from './lib/storage';
import { startStore, stopStore } from './lib/store';
import { AccountView } from './views/AccountView';
import { HabitView } from './views/HabitView';
import { HabitsView } from './views/HabitsView';
import { HistoryView } from './views/HistoryView';
import { ResetPasswordView } from './views/ResetPasswordView';
import { SignInView } from './views/SignInView';
import { TodayView } from './views/TodayView';

type Session =
  | { status: 'checking' }
  | { status: 'signed-out'; notice?: string }
  | { status: 'signed-in'; user: AppUser }
  | { status: 'unreachable' };

/** The signed-in user, so the app also starts offline. Removed with the rest on sign-out (store prefix). */
const USER_KEY = 'ph:u:me';

const TITLES: Record<string, string> = {
  today: 'Heute',
  history: 'Verlauf',
  habits: 'Habits',
  habit: 'Kalender',
  account: 'Konto',
  'reset-password': 'Passwort festlegen',
};

export function App() {
  const location = useLocation();
  const route = parseRoute(location.split('?')[0]!);
  const toast = useToast();
  const [session, setSession] = useState<Session>({ status: 'checking' });
  const [editor, setEditor] = useState<{ open: boolean; habit: Habit | null; template: HabitInput | null }>({
    open: false,
    habit: null,
    template: null,
  });

  const checkSession = useCallback(async () => {
    try {
      const { user } = await api.me();
      if (user) {
        writeJson(USER_KEY, user);
        setSession({ status: 'signed-in', user });
      } else {
        stopStore();
        setSession({ status: 'signed-out' });
      }
    } catch {
      const cached = readJson<AppUser>(USER_KEY);
      setSession(cached ? { status: 'signed-in', user: cached } : { status: 'unreachable' });
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useEffect(
    () =>
      onUnauthorized(() => {
        stopStore();
        setSession((s) => (s.status === 'signed-in' ? { status: 'signed-out', notice: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.' } : s));
      }),
    [],
  );

  const user = session.status === 'signed-in' ? session.user : null;
  useEffect(() => {
    if (user) startStore(user.id);
  }, [user]);

  useEffect(() => {
    document.title = `${TITLES[route.name] ?? 'Platypus'} · Platypus Habit`;
  }, [route.name]);

  const editorApi = useMemo<EditorApi>(
    () => ({
      openNew: (template) => setEditor({ open: true, habit: null, template: template ?? null }),
      openEdit: (habit) => setEditor({ open: true, habit, template: null }),
    }),
    [],
  );

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      toast({ text: 'Abmelden hat nicht geklappt. Bitte versuche es erneut.', kind: 'error' });
      return;
    }
    stopStore();
    setSession({ status: 'signed-out' });
    navigate('/', { replace: true });
  };

  let content: ReactNode;
  if (route.name === 'reset-password') {
    content = (
      <ResetPasswordView
        onDone={() => {
          stopStore();
          setSession({ status: 'signed-out', notice: 'Dein Passwort ist gespeichert. Melde dich jetzt an.' });
          navigate('/', { replace: true });
        }}
        onRequestNewLink={() => {
          setSession({ status: 'signed-out' });
          navigate('/', { replace: true });
        }}
      />
    );
  } else if (session.status === 'checking') {
    content = (
      <main className="splash" aria-busy="true">
        <Logo size={72} className="splash__logo" />
        <LoaderCircle size={22} className="spin splash__spinner" aria-label="Wird geladen" />
      </main>
    );
  } else if (session.status === 'unreachable') {
    content = (
      <main className="auth">
        <section className="auth__card glass glass--strong">
          <Logo size={64} className="auth__logo" />
          <h1 className="auth__title">Keine Verbindung</h1>
          <p className="auth__sub">Der Server ist gerade nicht erreichbar. Prüfe deine Internetverbindung.</p>
          <button type="button" className="btn btn--primary btn--block" onClick={() => void checkSession()}>
            <RotateCcw size={18} /> Erneut versuchen
          </button>
        </section>
      </main>
    );
  } else if (session.status === 'signed-out') {
    content = <SignInView notice={session.notice} onSignedIn={() => void checkSession()} />;
  } else {
    const signedIn = session.user;
    content = (
      <Shell route={route} user={signedIn} onSignOut={() => void handleSignOut()}>
        {route.name === 'today' && <TodayView />}
        {route.name === 'history' && <HistoryView />}
        {route.name === 'habits' && <HabitsView user={signedIn} onSignOut={() => void handleSignOut()} />}
        {route.name === 'habit' && <HabitView key={route.id} id={route.id} />}
        {route.name === 'account' && <AccountView user={signedIn} onSignOut={() => void handleSignOut()} />}
        {route.name === 'not-found' && <NotFound />}
      </Shell>
    );
  }

  return (
    <EditorContext.Provider value={editorApi}>
      <Backdrop />
      {content}
      {user && (
        <HabitEditor
          open={editor.open}
          habit={editor.habit}
          template={editor.template}
          onClose={() => setEditor((e) => ({ ...e, open: false }))}
        />
      )}
    </EditorContext.Provider>
  );
}

function NotFound() {
  return (
    <div className="view">
      <section className="notice glass">
        <p>Diese Seite gibt es nicht.</p>
        <button type="button" className="btn btn--glass btn--sm" onClick={() => navigate('/', { replace: true })}>
          Zu „Heute“
        </button>
      </section>
    </div>
  );
}
